import { FC, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
import { KTSVG } from '../../../../_metronic/helpers';
import { useConfirmAction } from '../../../hooks/utils/useConfirmAction';
import { showErrorAlert } from '../../../helpers/alert';
import { ExperienceSummary } from '../../teamExperiences/models/ExperienceSummaryModel';
import { formatDateShortIntl2 } from '../../../helpers/FormatDate';
import { getMaxPeople } from '../../experienceHostPreview/components/utils';
import { GenericModel } from '../../teamExperiences/models/GenericModel';
import { InviteePrivateAutocompleteField } from './fields/InviteePrivateAutocompleteField';
import { getGuestsExperiences, sendExperienceInvite } from '../../../services/teamExperienceService';
import { defaultProfileImage } from '../../../utils/constants';
import { formatDurationLabel, formatUsdAmount, pickDurationName } from '../../../utils/dateValidations';

type Props = {
    show: boolean;
    onClose: () => void;
    onSubmit?: () => void;
    onLoad?: (isloaded: boolean) => void;
    experience:ExperienceSummary|null;
    durations:GenericModel[]
}

export const InviteMemberModal: FC<Props> = ({ show, onClose, onLoad, onSubmit, experience, durations }) => {
    const [inviteeIds,setInviteeIds] = useState<number[]>([]);
    const confirm = useConfirmAction()
    const [members,setMembers] = useState<any[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setMembers([]);
            if (experience?.id) {
                const list = await getGuestsExperiences(experience.id);
                const users:any[] = [...list.confirmedPayment,...list.pendingPayment,...list.confirmedAttendance,...list.pendingAttendanceConfirmation];
                setMembers(users);
            }
        }
        fetchData();
    },[])

    const handleAddMembers = async () => {
        if(inviteeIds && inviteeIds.length>0){
            let isAdd = await confirm({ title: 'Add Members', message: 'Do you want to add these users?' });
            if(isAdd){
                //showErrorAlert({ title: 'Coming soon.', message: '' });
                onLoad?.(true);
                await sendExperienceInvite(experience?.id, inviteeIds)
                onLoad?.(false);
                showErrorAlert({title:'Users added',message:'The users have been added successfully.'})
                onSubmit?.();
            }
        }else{
            showErrorAlert({title:'No users selected',message:'Please select at least one user to continue.'})
        }
    }

    return (
        <Modal 
            show={show} 
            onHide={onClose} 
            centered 
            backdrop="static" 
            dialogClassName='experience-add-member-to-experience'
        >
            <div className='nb-invite-modal'>
                {/* CLOSE BUTTON */}
                <button className='nb-invite-modal__close' onClick={onClose}>
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                        <circle cx='12' cy='12' r='10' stroke='#808080' strokeWidth='1' />
                        <path d='M9.17 9.17l5.66 5.66M14.83 9.17l-5.66 5.66' stroke='#808080' strokeWidth='1' />
                    </svg>
                </button>

                {/* TITLE */}
                <h2 className='nb-invite-modal__title'>Invite Member</h2>

                {/* CONTENT */}
                <div className='nb-invite-modal__content'>
                    {/* HOSTS */}
                    <div className='nb-invite-modal__hosts'>
                        <div className='nb-invite-modal__host'>
                            <img src={experience?.host?.profilePicture ?? defaultProfileImage} onError={(e) => {e.currentTarget.src = defaultProfileImage;}} alt="You" />
                            <span>You</span>
                        </div>
                        {(members ?? []).map((m: any) => (
                            <div key={m.id} className='nb-invite-modal__host'>
                                <img src={m?.profilePicture ?? defaultProfileImage} onError={(e) => {e.currentTarget.src = defaultProfileImage;}} alt={m.fullName || `${m.firstName} ${m.lastName}`} />
                                <span>{m.fullName || `${m.firstName} ${m.lastName}`}</span>
                            </div>
                        ))}
                    </div>

                    {/* EXPERIENCE INFO */}
                    <div className='nb-invite-modal__experience'>
                        <div className='nb-invite-modal__label'>Invite Only</div>
                        <h3 className='nb-invite-modal__experience-title'>{experience?.title ?? ''}</h3>
                    </div>

                    {/* DETAILS */}
                    <div className='nb-invite-modal__details'>
                        <div className='nb-invite-modal__details-row'>
                            <div className={`nb-invite-modal__detail ${!experience?.locationAddress ? 'nb-invite-modal__detail--hidden' : ''}`}>
                                {/* Location Icon slot */}
                                <KTSVG path='/media/svg/nobilis/location_mark.svg' />
                                <span>{experience?.locationAddress ?? ''}</span>
                            </div>
                            <div className={`nb-invite-modal__detail ${(!experience?.dates || experience?.dates?.length === 0) ? 'nb-invite-modal__detail--hidden' : ''}`}>
                                {/* Calendar Icon slot */}
                                <KTSVG path='/media/svg/nobilis/calendar02.svg' />
                                <span>{experience?.dates?.length > 0 ? formatDateShortIntl2(experience?.dates[0]?.start) : 'N/A'}</span>
                            </div>
                            <div className={`nb-invite-modal__detail ${!getMaxPeople(experience) ? 'nb-invite-modal__detail--hidden' : ''}`}>
                                {/* People Icon slot */}
                                <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                                    <circle cx='7' cy='3' r='2.915' stroke='#151515' strokeWidth='1' fill='none' />
                                    <path
                                        d='M1.99 12.83c0-2.25 2.24-4.08 5.01-4.08s5.01 1.83 5.01 4.08'
                                        stroke='#151515'
                                        strokeWidth='1'
                                        fill='none'
                                    />
                                </svg>
                                <span>Max {getMaxPeople(experience) ?? 'N/A'} Guests</span>
                            </div>
                        </div>

                        <div className='nb-invite-modal__details-row'>
                            <div className={`nb-invite-modal__detail ${!experience?.pricePerGuest ? 'nb-invite-modal__detail--hidden' : ''}`}>
                                {/* Currency Icon slot */}
                                <KTSVG path='/media/svg/nobilis/currency.svg' />
                                <span>{formatUsdAmount(experience?.pricePerGuest) || 'USD N/A'} / Pax</span>
                            </div>
                            <div className={`nb-invite-modal__detail ${!experience?.duration ? 'nb-invite-modal__detail--hidden' : ''}`}>
                                {/* Clock Icon slot */}
                                <KTSVG path='/media/svg/nobilis/clock.svg' />
                                <span>{formatDurationLabel(experience?.duration, pickDurationName(durations, experience?.durationType)) || 'N/A'}</span>
                            </div>
                            <div className='nb-invite-modal__detail nb-invite-modal__detail--hidden'>
                                <span>Hidden spacer</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* INPUT */}
                <div className='nb-invite-modal__input-group'>
                    <label className='nb-invite-modal__input-label'>Type Member’s Name, surname</label>
                    <div className='nb-invite-modal__input-wrapper'>
                        <InviteePrivateAutocompleteField values={inviteeIds} onChange={setInviteeIds} experience={experience}/>
                    </div>
                </div>

                {/* BUTTON */}
                <button className='nb-invite-modal__button' onClick={handleAddMembers}>
                    <span>invite members</span>
                    <KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                </button>
            </div>
        </Modal>
    );
}